import moment from "moment";
import React, { FC } from "react";
import { SingleDatePicker } from "react-dates";
import Button from "@laborhack/custom-button";
import { useAuth } from "../../../firebase/hooks/useAuthHook";
import { forceActualDay, isAuthorized } from "../../../helpers/helpers";
import { IInvoice } from "../../../types";

import { gql, useMutation } from "@apollo/client";

const CONFIRM_OFFLINE_INVOICE_PAYMENT = gql`
  mutation ConfirmOfflineInvoicePayment(
    $invoiceId: String!
    $amount: Float!
    $transactionDate: DateTimeISO!
    $transactionReference: String!
    $bankName: String!
  ) {
    confirmOfflineInvoicePayment(
      invoiceId: $invoiceId
      amount: $amount
      transactionDate: $transactionDate
      transactionReference: $transactionReference
      bankName: $bankName
    ) {
      id
    }
  }
`;

export interface LogofflinePaymentProps {
  invoice: IInvoice;
  expectedAmount: number;
  onClose: () => void;
}

export function canLogOfflinePayment(invoice: IInvoice, hasFinanceRole: boolean) {
  return hasFinanceRole && !invoice.canceled && !invoice.isFullyPaid && !!invoice.allowedPaymentProviders?.includes('offline');
}

export const LogOfflinePayment: FC<LogofflinePaymentProps> = ({
  invoice,
  expectedAmount,
  onClose: handleClose,
}) => {
  const { idTokenResult } = useAuth();
  const eligible = canLogOfflinePayment(invoice, isAuthorized({ FINANCE: true }, idTokenResult?.claims.roles));
  const submitting = React.useRef(false);
  const [focusedInput, setFocusedInput] = React.useState<boolean>(false);

  const [bankName, setBankName] = React.useState("");
  const [referenceNumber, setReferenceNumber] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [transactionDate, setTransactionDate] = React.useState<Date>();

  const [confirmOfflineInvoicePayment, { loading, error }] = useMutation(
    CONFIRM_OFFLINE_INVOICE_PAYMENT,
    {
      refetchQueries: ["GetInvoice"],
      onCompleted: () => {
        handleClose();
      },
    }
  );

  const handleSubmit = async () => {
    if (submitting.current || loading || !eligible || !transactionDate || !bankName.trim() || !referenceNumber.trim() || !Number.isFinite(Number(amount)) || Number(amount) <= 0) return;
    submitting.current = true;
    try {
      await confirmOfflineInvoicePayment({
      variables: {
        invoiceId: invoice.id,
        amount: parseFloat(amount),
        transactionDate,
        transactionReference: referenceNumber,
        bankName,
      },
      });
    } catch {
      // Apollo exposes the server error below without resetting the form.
    } finally {
      submitting.current = false;
    }
  };

  return (
    <div className='p-8'>
      <h3 className='text-primary-500 font-bold'>Log Offline Payment</h3>
      <p className='text-primary-300'>
        Record a bank transfer received for this invoice.
      </p>
      <p>Expected payment for the current balance or instalment: <strong>₦{Math.ceil(expectedAmount).toLocaleString('en-NG')}</strong></p>
      {error && <p role='alert' className='text-red-600'>{error.message}</p>}
      {!eligible && <p role='alert'>This invoice is not eligible for offline payment logging.</p>}
      <div className='w-full'>
        <div className='flex flex-col my-8'>
          <div className='mb-4'>
            <label className='block !text-primary-300 text-sm font-semibold mb-2'>
              LaborHack&apos;s Bank Name
            </label>
            <input
              className='pl-4 border border-primary-150 border-solid rounded text-primary-500 h-10'
              type='text'
              name='bank'
              placeholder='Bank Name'
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
            />
          </div>

          <div className='mb-4'>
            <label className='block !text-primary-300 text-sm font-semibold mb-2'>
              Reference Number
            </label>
            <input
              className='pl-4 border border-primary-150 border-solid rounded text-primary-500 h-10'
              type='text'
              name='reference'
              placeholder='Reference Number'
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </div>

          <div className='mb-4'>
            <label className='block !text-primary-300 text-sm font-semibold mb-2'>
              Amount
            </label>
            <input
              className='pl-4 border border-primary-150 border-solid rounded text-primary-500 h-10'
              type='text'
              name='amount'
              placeholder='Amount'
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className='mb-4'>
            <label className='block !text-primary-300 text-sm font-semibold mb-2'>
              Transaction Date
            </label>
            <SingleDatePicker
              id='transactionDate'
              date={transactionDate ? moment(transactionDate) : null}
              onDateChange={(date) => {
                date && setTransactionDate(forceActualDay(date.toDate()));
              }}
              focused={focusedInput}
              onFocusChange={({ focused }) => setFocusedInput(focused)}
              numberOfMonths={1}
              enableOutsideDays
              isOutsideRange={() => false}
            />
          </div>
        </div>
        <div className='flex w-full justify-end items-center'>
          <Button onClick={handleSubmit} disabled={loading || !eligible || !transactionDate || !bankName.trim() || !referenceNumber.trim() || !Number.isFinite(Number(amount)) || Number(amount) <= 0}>
            Log Payment
          </Button>
        </div>
      </div>
    </div>
  );
};
